import { useEffect, useId, useState } from "react";
import useLastSeed from "../hooks/useLastSeed";
import { validateLoad } from "../domain/performedRules";
import { sameLoad } from "../domain/setFacts";
import { isAllowedLoadIncrement, usableLastLoad } from "../domain/previousPerformance";
import {
  loadPresentation,
  standardOlympicBarWeight,
} from "../domain/executionUi";

function normalizeDecimal(value) {
  const trimmed = String(value ?? "").replace(",", ".");
  if (!trimmed) return null;
  if (!/^(?:\d+(?:\.\d{1,2})?|\.\d{1,2})$/.test(trimmed)) return undefined;
  const number = Number(trimmed);
  if (!Number.isFinite(number) || number > 2000) return undefined;
  return String(number);
}

function isLoadDraft(value) {
  return /^\d*(?:[.,]\d*)?$/.test(value);
}

// Spoken names keep the load-mode label meaningful once the set number is added
// ("Set 2 total weight", not "Set 2 total"). Visible labels are unchanged.
const SET_LOAD_NAMES = Object.freeze({
  Total: "total weight",
  Plates: "plates",
  "Per side": "weight per side",
  Added: "added weight",
  Assist: "assistance",
  "Weight optional": "optional weight",
});

function loadAccessibleName(label, setIndex) {
  if (setIndex == null) return label;
  return `Set ${setIndex} ${SET_LOAD_NAMES[label] || String(label || "weight").toLowerCase()}`;
}

export default function LoadField({
  profile,
  load,
  defaultUnit,
  preferences = {},
  setIndex = null,
  disabled = false,
  compact = false,
  // This set's mapped LAST, or null (§13.10). Only a load that can be entered
  // as-is in the field's current unit and convention is ever seeded.
  lastSet = null,
  onUnitChange = null,
  onCommit,
}) {
  const presentation = loadPresentation(profile, preferences, load);
  const [draft, setDraft] = useState(load?.value ?? "");
  const resolvedDefaultUnit = defaultUnit || preferences.weightUnit || null;
  const [unit, setUnit] = useState(load?.unit || resolvedDefaultUnit);
  const [error, setError] = useState("");
  const errorId = useId();

  useEffect(() => {
    setDraft(load?.value ?? "");
    setUnit(load?.unit || resolvedDefaultUnit);
  }, [load?.unit, load?.value, resolvedDefaultUnit]);

  useEffect(() => {
    onUnitChange?.(unit);
  }, [onUnitChange, unit]);

  const seedLoad = profile && profile.loadMode !== "NONE"
    ? usableLastLoad(lastSet, {
        loadMode: profile.loadMode,
        convention: presentation.convention,
        unit,
        barWeight: standardOlympicBarWeight(unit, preferences),
      })
    : null;
  const seed = useLastSeed({
    seedValue: seedLoad?.value ?? null,
    isEmpty: draft === "" && !load,
    disabled: disabled || !unit || !presentation.convention,
    setDraft,
  });

  if (!profile || profile.loadMode === "NONE") return null;

  function buildLoad(value, nextUnit = unit) {
    if (value == null) return null;
    if (!["LB", "KG"].includes(nextUnit) || !presentation.convention) return undefined;
    const preserveStoredBarWeight = profile.loadMode === "OLYMPIC_BARBELL" &&
      load?.convention === "PER_SIDE" && load.unit === nextUnit;
    return {
      value,
      unit: nextUnit,
      convention: presentation.convention,
      barWeight: profile.loadMode === "OLYMPIC_BARBELL" &&
        presentation.convention === "PER_SIDE"
        ? preserveStoredBarWeight
          ? load.barWeight
          : standardOlympicBarWeight(nextUnit, preferences)
        : null,
    };
  }

  function commitValue() {
    seed.release();
    const value = normalizeDecimal(draft);
    if (value === undefined) {
      setError("Finish entering a weight from 0 to 2000.");
      return;
    }
    if (!isAllowedLoadIncrement(value, profile.loadMode, presentation.convention, unit)) {
      const step = profile.loadMode === "OLYMPIC_BARBELL" && presentation.convention === "PER_SIDE"
        ? unit === "KG" ? "1.25 kg" : "2.5 lb"
        : "0.5";
      setError(`Use ${step} increments.`);
      return;
    }
    const next = buildLoad(value);
    if (value !== null && !next) {
      setError("Choose your workout load preferences before entering weight.");
      return;
    }
    if (next && !validateLoad(next, profile.loadMode)) {
      setError("This load value is not valid for this exercise.");
      return;
    }
    setError("");
    if (sameLoad(next, load)) {
      // Passive blur or a semantically equal value is not a commit (§9.4).
      setDraft(load?.value ?? "");
      return;
    }
    setDraft(value ?? "");
    onCommit(next);
  }

  function updateDraft(nextDraft) {
    if (!isLoadDraft(nextDraft)) return;
    setError("");
    setDraft(nextDraft);
  }

  function relabelUnit() {
    if (!["LB", "KG"].includes(unit)) return;
    const nextUnit = unit === "LB" ? "KG" : "LB";
    const value = normalizeDecimal(draft);
    const relabelsStoredValue = value != null && load?.unit === unit &&
      normalizeDecimal(load.value) === value;
    if (
      value !== undefined && value !== null && !relabelsStoredValue &&
      !isAllowedLoadIncrement(value, profile.loadMode, presentation.convention, nextUnit)
    ) {
      const step = profile.loadMode === "OLYMPIC_BARBELL" && presentation.convention === "PER_SIDE"
        ? nextUnit === "KG" ? "1.25 kg" : "2.5 lb"
        : "0.5";
      setError(`Use ${step} increments.`);
      return;
    }
    setUnit(nextUnit);
    if (value !== undefined && value !== null) {
      const next = buildLoad(value, nextUnit);
      if (validateLoad(next, profile.loadMode)) {
        setError("");
        onCommit(next);
      }
    }
  }

  return (
    <label className="lz-workout-load-field">
      <span className={compact ? "lz-v2-visually-hidden" : "lz-workout-field-label"}>{presentation.label}</span>
      <span className="lz-workout-load-field__control">
        <input
          type="text"
          inputMode="decimal"
          value={draft}
          placeholder="--"
          disabled={disabled || !unit || !presentation.convention}
          aria-label={loadAccessibleName(presentation.label, setIndex)}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          ref={seed.inputRef}
          onClick={seed.activate}
          onChange={(event) => updateDraft(seed.resolveChange(event.target.value))}
          onBlur={commitValue}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur();
          }}
        />
        <button
          type="button"
          disabled={disabled || !unit || !presentation.convention}
          onClick={relabelUnit}
          aria-label={unit
            ? `Change ${setIndex == null ? "" : `set ${setIndex} `}unit from ${unit.toLowerCase()}`
            : "Weight unit unresolved"}
        >
          <span className="lz-workout-load-field__unit-label">
            {unit?.toLowerCase() || "—"}
          </span>
        </button>
      </span>
      {error ? <span id={errorId} className="lz-workout-field-error" role="alert">{error}</span> : null}
    </label>
  );
}

export { isAllowedLoadIncrement, isLoadDraft, normalizeDecimal };
